import { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

export const FAQSection = () => {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState(0);
  const faqs = t("faq.items");

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <section
      id="faq"
      className="
        section-padding
        bg-white
        dark:bg-[#141617]
        transition-colors duration-300
      "
      data-testid="faq-section"
    >
      <div className="container-width">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#5cb85c] font-semibold text-sm tracking-wider mb-3 block">
              {t("faq.sectionTag")}
            </span>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white transition-colors">
              {t("faq.title")}
            </h2>
          </motion.div>

          {/* FAQ Items */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="
                  rounded-xl overflow-hidden
                  bg-white
                  dark:bg-black/30
                  border border-slate-100
                  dark:border-white/10
                  transition-colors duration-300
                "
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  type="button"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                  className="
                    w-full flex items-center justify-between
                    p-5 text-left
                    hover:bg-slate-50
                    dark:hover:bg-white/5
                    transition-colors
                  "
                  data-testid={`faq-toggle-${index}`}
                >
                  <span className="text-lg font-semibold text-slate-900 dark:text-white pr-4 transition-colors">
                    {faq.q}
                  </span>

                  <div
                    aria-hidden="true"
                    className={`
                      flex-shrink-0 w-8 h-8 rounded-full
                      flex items-center justify-center
                      transition-colors
                      ${
                        openIndex === index
                          ? "bg-[#5cb85c] text-white"
                          : "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300"
                      }
                    `}
                  >
                    {openIndex === index ? (
                      <Minus size={18} />
                    ) : (
                      <Plus size={18} />
                    )}
                  </div>
                </button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      id={`faq-answer-${index}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p
                        className="
                          px-5 pb-5
                          text-slate-600
                          dark:text-slate-300
                          leading-relaxed
                          transition-colors
                        "
                        data-testid={`faq-answer-${index}`}
                      >
                        {faq.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* subtle edge highlight */}
                <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-black/5 dark:ring-white/5 transition-colors" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
